<template>
  <div v-if="entity">
    <div class="dev-row">
      <div class="dev-info">
        <span class="dev-label">Dev wallet</span>
        <span class="dev-value">{{ devLabel }}</span>
      </div>

      <button
        class="dev-edit-btn"
        type="button"
        :disabled="!canEditDev"
        :title="isLaunched ? 'Dev wallet is locked after launch' : ''"
        @click="startDevEdit"
      >
        {{ devWallet ? 'Change' : 'Set' }}
      </button>
    </div>

    <p v-if="isLaunched" class="hint">
      Dev wallet is locked after launch
    </p>

    <div v-if="editingDev" class="dev-edit-row">
      <div class="dev-edit-main">
        <label class="dev-label">Dev wallet</label>
        <select v-model="devDraft" class="dev-control">
          <option value="">Not set</option>
          <option
            v-for="w in devWalletOptions"
            :key="w.public_key || w.publicKey"
            :value="w.public_key || w.publicKey"
          >
            {{ w.name || shortKey(w.public_key || w.publicKey) }}
          </option>
        </select>
      </div>

      <div class="dev-edit-actions">
        <button type="button" class="btn small primary" @click="saveDev">
          Save
        </button>
        <button type="button" class="btn small ghost" @click="cancelDevEdit">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { shortKey } from './utils'

const props = defineProps({
  entity: { type: Object, default: null },
  isLaunched: { type: Boolean, default: false },
  devWallet: { type: String, default: '' },
  devWalletOptions: { type: Array, default: () => [] },
})

const emit = defineEmits(['own'])

const editingDev = ref(false)
const devDraft = ref(props.devWallet || '')

watch(
  () => props.devWallet,
  v => { if (!editingDev.value) devDraft.value = v || '' }
)

const canEditDev = computed(() => {
  if (!props.entity) return false
  if (props.isLaunched) return false
  return true
})

const devWalletObj = computed(() => {
  const pk = props.devWallet
  if (!pk) return null
  return props.devWalletOptions.find(w => w && (w.public_key === pk || w.publicKey === pk)) || null
})

const devLabel = computed(() => {
  const pk = props.devWallet
  if (!pk) return 'Not set'
  const w = devWalletObj.value
  return w?.name || shortKey(pk)
})

function startDevEdit() {
  if (!canEditDev.value) return
  devDraft.value = props.devWallet || ''
  editingDev.value = true
}

function cancelDevEdit() {
  editingDev.value = false
  devDraft.value = props.devWallet || ''
}

function saveDev() {
  if (!canEditDev.value) return
  const value = devDraft.value || ''
  emit('own', value || null)
  editingDev.value = false
}
</script>

<style scoped>
.dev-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
}

.dev-info{
  display:flex;
  flex-direction:column;
  gap:2px;
}

.dev-label{
  font-size:11px;
  color:#9ca3af;
}

.dev-value{
  font-size:13px;
  color:#e5e7eb;
}

.dev-edit-btn{
  flex-shrink:0;
  padding:2px 8px;
  border-radius:999px;
  border:1px solid #333;
  background:#0b0b0b;
  color:#e5e7eb;
  font-size:11px;
  cursor:pointer;
}
.dev-edit-btn:hover{ background:#111827; }
.dev-edit-btn:disabled{ opacity:0.5; cursor:default; }

.dev-edit-row{
  display:flex;
  flex-direction:column;
  gap:6px;
  margin-top:6px;
}

.dev-control{
  width:100%;
  padding:4px 8px;
  border-radius:6px;
  border:1px solid #333;
  background:#0b0b0b;
  color:#e5e7eb;
  font-size:12px;
}
.dev-control:focus{
  outline:none;
  border-color:#6366f1;
}

.dev-edit-actions{ display:flex; gap:6px; }

.btn{
  border:1px solid #333;
  border-radius:6px;
  cursor:pointer;
}
.btn.small{ width:auto; font-size:11px; padding:3px 8px; }
.btn.small.primary{ background:#2563eb; border-color:#2563eb; color:#fff; }
.btn.small.ghost{ background:transparent; color:#aaa; }

.hint{
  margin-top:6px;
  font-size:11px;
  color:#6b7280;
}
</style>
